import { useMemo, useState } from 'react';
import { useQueryClient } from '@tanstack/react-query';
import { Callout, ConfirmationModal, TextField, Tooltip, Typography, useOverlayContext } from 'seagull';
import { RichTextEditor, extractPureTextFromRichText } from 'seagull/components/RichTextEditor/RichTextEditor';
import { getTestIds } from 'seagull/utils/TestIds';
import { useLegalTerms } from "../../../hooks/reactQuery/queries/useLegalTerms";
import { QueryKeys } from "../../../hooks/reactQuery/queryKeys";
import { ErrorResponse, ServerError } from "../../../infra/ErrorResponse";
import { handleRequest } from "../../../infra/utils";
import { useStoreActions } from "../../../store/store-context";
import styles from "./EditDocumentModal.module.scss";
import { jsx as _jsx, jsxs as _jsxs } from "react/jsx-runtime";
const MAX_NAME_LENGTH = 80;
const MAX_CONTENT_LENGTH = 100000;
export const {
  EditDocumentModalTID
} = getTestIds();
const getTitle = variant => {
  if (variant.type === 'global') {
    return 'Edit document';
  }
  return variant.context === 'proposal' ? 'Edit document for this proposal' : 'Edit document for this agreement';
};
const getInfoBannerText = variant => {
  if (variant.type === 'global') {
    return 'Updating the document will not affect existing agreements or sent proposals.';
  }
  return variant.context === 'proposal' ? "You're updating the document for this specific proposal, and these changes won't affect your global version." : "You're updating the document for this specific agreement, and these changes won't affect your global version.";
};
const getSaveButtonLabel = variant => {
  if (variant.type === 'global') {
    return 'Save';
  }
  return variant.context === 'proposal' ? 'Save for this proposal' : 'Save for this agreement';
};
export const EditDocumentModal = props => {
  const {
    variant,
    legalTermsId,
    initialName = '',
    initialContent,
    dismissModal
  } = props;
  const isLocalVariant = variant.type === 'local';
  const queryClient = useQueryClient();
  const {
    api
  } = useStoreActions();
  const {
    legalTerms
  } = useLegalTerms();
  const [name, setName] = useState(initialName);
  const [error, setError] = useState(undefined);
  const [content, setContent] = useState(initialContent);
  const [isSaving, setIsSaving] = useState(false);
  const {
    showErrorToast,
    showSuccessToast
  } = useOverlayContext();
  const existingNames = useMemo(() => new Set(legalTerms.filter(term => term.name.toLowerCase() !== initialName.toLowerCase()).map(term => term.name.toLowerCase())), [legalTerms, initialName]);
  const isDuplicateName = existingNames.has(name.trim().toLowerCase());
  const isNameTooLong = name.length > MAX_NAME_LENGTH;
  const contentText = content ? extractPureTextFromRichText(content) : '';
  const isContentTooLong = contentText.length > MAX_CONTENT_LENGTH;
  const isNameEmpty = !name.trim();
  const hasNameError = !!error || isDuplicateName || isNameTooLong;
  const hasContentError = isContentTooLong;
  const hasError = hasNameError || hasContentError;
  const isNameChanged = name !== initialName;
  const isContentChanged = JSON.stringify(content === null || content === void 0 ? void 0 : content.value) !== JSON.stringify(initialContent === null || initialContent === void 0 ? void 0 : initialContent.value);
  const hasChanges = isNameChanged || isContentChanged;
  const nameHelperText = useMemo(() => {
    if (isDuplicateName || error === ServerError.LegalTermsNameAlreadyExists) {
      return 'Document name already exists. Please use a different one.';
    } else if (isNameTooLong) {
      return 'Document name can be up to 80 characters';
    }
  }, [isDuplicateName, isNameTooLong, error]);
  const contentHelperText = isContentTooLong ? 'Document content can be up to 100,000 characters' : undefined;
  const handleSave = async () => {
    if (hasError || isNameEmpty) {
      return;
    }
    setIsSaving(true);
    const contentString = content ? JSON.stringify(content.value) : '';
    const result = await handleRequest(api.business.updateQuillLegalTerms(legalTermsId, {
      name: name.trim(),
      content: contentString
    }));
    if (!result.success) {
      if (result.error instanceof ErrorResponse && result.error.hasServerError(ServerError.LegalTermsNameAlreadyExists)) {
        setError(ServerError.LegalTermsNameAlreadyExists);
      } else {
        showErrorToast('An unknown error has occurred.');
      }
      setIsSaving(false);
      return;
    }
    showSuccessToast('Document updated successfully');
    dismissModal();
    queryClient.invalidateQueries({
      queryKey: [QueryKeys.LegalTerms]
    });
  };
  const nameField = /*#__PURE__*/_jsx(TextField, {
    collapseHelperText: true,
    testId: EditDocumentModalTID.NameInput(),
    label: "Document name",
    value: name,
    onChange: e => {
      setName(e.target.value);
      setError(undefined);
    },
    error: hasNameError,
    helperText: nameHelperText,
    helperTextReserveSpace: true,
    width: "100%",
    disabled: isLocalVariant
  });
  return /*#__PURE__*/_jsx(ConfirmationModal, {
    title: getTitle(variant),
    width: "800px",
    height: "fit-content",
    testId: EditDocumentModalTID.Root(),
    textAlignCenter: false,
    content: /*#__PURE__*/_jsxs("div", {
      className: styles.contentWrapper,
      children: [/*#__PURE__*/_jsx(Callout, {
        intent: "info",
        testId: EditDocumentModalTID.InfoBanner(),
        withBorder: true,
        classes: {
          wrapper: styles.calloutWrapper
        },
        children: getInfoBannerText(variant)
      }), isLocalVariant ? /*#__PURE__*/_jsx(Tooltip, {
        title: "To edit the document name, update it in your default proposal settings.",
        placement: "top",
        children: nameField
      }) : nameField, /*#__PURE__*/_jsxs("div", {
        className: styles.richTextEditorWrapper,
        children: [/*#__PURE__*/_jsx(Typography, {
          variant: "paragraph2",
          skin: "secondary",
          className: styles.editorLabel,
          children: "Document content"
        }), /*#__PURE__*/_jsx(RichTextEditor, {
          testId: EditDocumentModalTID.ContentEditor(),
          editorHeight: 300,
          initialValue: initialContent,
          error: hasContentError,
          helperText: contentHelperText,
          onChange: value => setContent(value)
        })]
      })]
    }),
    primaryButtonLabel: getSaveButtonLabel(variant),
    secondaryButtonLabel: "Cancel",
    primaryButtonDisabled: hasError || isNameEmpty || !hasChanges,
    primaryButtonLoading: isSaving,
    onPrimaryButtonClick: handleSave,
    onSecondaryButtonClick: () => dismissModal()
  });
};